import { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Clock, MessageSquare, Shield, Users } from "lucide-react";

import { getProjectAnalytics, MetricSeries } from "./api";
import {
  StatCard,
  StatCardDim,
  StatCardError,
  StatCardSkeleton,
  StatDelta,
} from "./StatCard";
import styles from "./AnalyticsCards.module.css";

const NUM = new Intl.NumberFormat("en-US");

interface AnalyticsCardsProps {
  projectId: string;
  /**
   * Why this row is not counting, when it is not. Omitted, it counts — which
   * is what a caller that knows of no wipe does, and what every caller did
   * before wipes existed.
   *
   * `"wiping"` dims the row and says so on the first card: every figure here
   * is a count of rows being deleted as they are counted, so the endpoint
   * would answer with a number that was never true — and answer it into a
   * ten-minute server cache, which is how a wiped project goes on reporting
   * 128,430 players afterwards.
   *
   * `"unknown"` is the page still finding out. It holds the skeleton rather
   * than asking, because the request would race the answer — and the case it
   * would lose is precisely the one this screen exists for: a page opened
   * onto a wipe already in flight.
   */
  paused?: "wiping" | "unknown";
  /**
   * When this project's latest wipe began, as the server timed it. Omitted
   * when it has never been wiped.
   *
   * Figures the server computed before that instant count rows the wipe has
   * since deleted, and the row says nothing rather than print them — see
   * `preWipe` below.
   */
  wipeStartedAt?: string;
}

// countDelta builds the delta line for Players / Guilds: a green "+N new"
// when there is data, a muted "No <noun> yet" when the project is empty.
function countDelta(m: MetricSeries, emptyText: string): StatDelta {
  if ((m.total ?? 0) === 0) {
    return { tone: "none", context: emptyText };
  }
  return {
    tone: "up",
    value: `+${NUM.format(m.last_7d)}`,
    context: "new · last 7 days",
  };
}

/**
 * AnalyticsCards renders the game-level analytics row on Project Overview:
 * Players / Guilds / Chat over a fixed 7-day window. Loads independently of
 * the rest of the page; an error degrades to em-dashes without breaking it.
 * Visual reference: docs/mockups/project-overview.html + analytics-states.html.
 */
export function AnalyticsCards({
  projectId,
  paused,
  wipeStartedAt,
}: AnalyticsCardsProps) {
  const q = useQuery({
    queryKey: ["project", projectId, "analytics"],
    queryFn: () => getProjectAnalytics(projectId),
    enabled: !!projectId && !paused,
  });

  /**
   * A payload the server computed before this project's wipe began.
   *
   * The pause stops the row asking; it cannot stop it holding. When the wipe
   * ends and the query re-enables, react-query hands back the payload it had
   * before — so the counts of a project that has just been emptied appear for
   * the frames before the refetch lands, under the toast announcing they are
   * gone. This is what keeps the skeleton up instead.
   *
   * The comparison is between two of the SERVER's own clocks — `generated_at`
   * against the task's `started_at` — never the browser's `dataUpdatedAt`.
   * Not only to avoid comparing two machines' clocks: this endpoint is cached
   * for ten minutes, so "fetched a second ago" says nothing about when the
   * rows were counted, and a payload computed nine minutes before the wipe
   * would sail past a fetched-at test.
   */
  const preWipe =
    !!wipeStartedAt &&
    !!q.data &&
    Date.parse(q.data.generated_at) < Date.parse(wipeStartedAt);
  // Everything below reads this and not `q.data`: what the row may print is
  // what it may be counted on, captions included.
  const data = preWipe ? undefined : q.data;

  let body: ReactNode;
  if (paused === "wiping") {
    body = (
      <>
        <StatCardDim
          label="Players"
          icon={<Users size={14} />}
          note="counting stops during a wipe"
        />
        <StatCardDim label="Guilds" icon={<Shield size={14} />} />
        <StatCardDim
          label="Chat messages"
          icon={<MessageSquare size={14} />}
        />
      </>
    );
  } else if (q.isError) {
    body = (
      <>
        <StatCardError label="Players" icon={<Users size={14} />} />
        <StatCardError label="Guilds" icon={<Shield size={14} />} />
        <StatCardError label="Chat messages" icon={<MessageSquare size={14} />} />
      </>
    );
  } else if (!data) {
    // No data and no error: in flight, held back by `paused: "unknown"`, or
    // holding figures counted before a wipe. All three are "we do not know
    // yet", and the skeleton is what that looks like.
    body = (
      <>
        <StatCardSkeleton variant={0} />
        <StatCardSkeleton variant={1} />
        <StatCardSkeleton variant={2} />
      </>
    );
  } else {
    const a = data;
    const chatDelta: StatDelta =
      a.chat.last_7d === 0
        ? { tone: "none", context: "No messages · last 7 days" }
        : { tone: "flat", context: "messages · last 7 days" };
    body = (
      <>
        <StatCard
          label="Players"
          icon={<Users size={14} />}
          value={NUM.format(a.players.total ?? 0)}
          delta={countDelta(a.players, "No players yet")}
          series={a.players.series}
        />
        <StatCard
          label="Guilds"
          icon={<Shield size={14} />}
          value={NUM.format(a.guilds.total ?? 0)}
          delta={countDelta(a.guilds, "No guilds yet")}
          series={a.guilds.series}
        />
        <StatCard
          label="Chat messages"
          icon={<MessageSquare size={14} />}
          value={NUM.format(a.chat.last_7d)}
          delta={chatDelta}
          series={a.chat.series}
        />
      </>
    );
  }

  // Caption matches the mockup's three states (docs/mockups: project-overview.html
  // populated row; analytics-states.html loading & empty rows) plus the
  // `bots excluded` link t-147 adds (project-overview-analytics.html).
  //
  // That link is in BOTH settled states and not only the populated one. The
  // empty row is where it earns its place: a project running nothing but the
  // simulation prints three zeros here, and without the caption saying what is
  // not being counted, those zeros read as a broken page rather than as the
  // answer to «how much of this is real».
  const allEmpty =
    !!data &&
    (data.players.total ?? 0) === 0 &&
    (data.guilds.total ?? 0) === 0 &&
    data.chat.last_7d === 0;
  let caption = "Game-level analytics · last 7 days";
  if (data) {
    caption = allEmpty
      ? "Game-level analytics · last 7 days · bots excluded · data appears once players connect via the SDK"
      : "Game-level analytics · last 7 days · bots excluded · computed on page open, cached ~10 min";
  }

  // While a wipe runs there is no line under the row at all: the caption's job
  // is to say where the numbers came from, and the one thing worth saying —
  // that nothing is being counted — is already on the first card.
  let footer: ReactNode = null;
  if (paused !== "wiping") {
    footer =
      // Only a query that actually failed says so. One that was never allowed
      // to ask has not failed, and "Couldn't load analytics" during a wipe
      // sends the operator hunting for a second problem.
      q.isError ? (
        <div className={styles.errorStrip} role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>Couldn&apos;t load analytics. The rest of this page still works.</span>
          <button
            type="button"
            className={styles.retry}
            onClick={() => q.refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className={styles.caption}>
          <Clock size={12} aria-hidden="true" />
          {caption}
        </div>
      );
  }

  return (
    <div>
      {/* With nothing under it the row owes the page the 16px the caption
          would have carried. */}
      <div className={`${styles.row} ${footer ? "" : styles.rowAlone}`}>
        {body}
      </div>
      {footer}
    </div>
  );
}
